"use client";

// [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → now central widget_impression
// (TrackedWidget in NewPage); see webapp/docs/analytics
// import { FireWidgetViewedEvent } from "@/customAnalytics/trackingEvents";
import StarRating from "@/components/atoms/StarRating";
import { useIsMobile } from "@/hooks/useIsMobile";
import { cn } from "@/lib/utils";
import Image from "next/image";
import { RootState } from "@/store/store";
import { hexToRgba } from "@/utils/utils";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  LEAD_DROP_FORM_MOBILE_CTA_VARIANTS,
  LEAD_DROP_FORM_SECTION_VARIANTS,
  LEAD_DROP_FORM_VARIANTS,
} from "../../../constants";
import RevealOnScroll from "@/components/atoms/RevealOnScroll";
import Carousel1 from "@/components/organisms/carousels/Carousel1";
import YouTubeVideoOverlay from "@/components/atoms/YouTubeVideoOverlay";

/** Alumni avatars shown in the rating strip, matching Banner 2's cap. */
const MAX_LEARNER_AVATARS = 3;

/**
 * Media for the right-hand slot — a video when one is configured, otherwise the
 * image. Fills the column, which already carries the per-breakpoint width, so
 * the widget's existing spacing is untouched.
 *
 * Rendered server-side (unlike the lead form, which needs the client): on the
 * media option this is the section's largest paintable element, so keeping it in
 * the initial HTML is what makes the LCP fix worthwhile.
 */
/** Auto-advance interval; About 2 has no `slide_frequency` column, so this
 *  mirrors Banner 2's default rather than adding a tenth column. */
const CAROUSEL_INTERVAL_MS = 5000;
/** Carousel slides, capped like Banner 2's hero. */
const MAX_CAROUSEL_SLIDES = 3;

/**
 * Media for the right-hand slot, matching Banner 2's hero behaviour: an
 * Embla-backed carousel (swipeable, auto-advancing, dots) with optional video
 * per slide.
 *
 * The box is square (`aspect-square` against the column's 400 / 440 / 508
 * widths), so rotation can never resize it.
 *
 * Both carousel images and video use `object-cover` so they fill the square
 * edge to edge. Uploaded artwork should therefore be square: `cover` crops the
 * overflowing axis, so a portrait or landscape upload loses its outer edges —
 * which matters here because the art carries its own baked-in border rather
 * than getting one from CSS.
 */
const AboutMedia = ({
  slides,
  alt,
  video,
  onVideoPlay,
}: {
  slides: any[];
  alt?: string;
  /** Widget-level video. When set it replaces the carousel outright — no slides,
   *  no dots — exactly as `hero_video_url` does on Banner 2. */
  video?: string;
  onVideoPlay: (url: string) => void;
}) => {
  const [index, setIndex] = useState(0);
  if (!video && slides.length === 0) return null;

  /* `rounded-[40px]` on the media itself as well as the container: the corners
     are then correct even for a media element the container's `overflow-hidden`
     cannot clip on its own (a cross-origin <video>, for one). */
  const mediaClass = "h-full w-full rounded-[40px] object-cover";

  const renderSlide = (slide: any, i: number) => (
    <div
      key={slide.id ?? i}
      /* Sizing matches Banner 2's slide wrapper: Carousel1 lays its children
         out itself, so a full-width/full-height box is all each slide needs.
         Adding flex-basis here fought Embla's own sizing and showed two
         slides at once. */
      className="relative aspect-square w-full"
    >
      {/* The image always renders. A slide's video is a LINK opened in the
          shared overlay, never an inline file that replaces the image — this is
          what Banner 2 does, and getting it wrong was the original bug here. */}
      <Image
        src={slide.image}
        alt={slide.alt_image || alt || ""}
        width={508}
        height={600}
        priority={i === 0}
        className={mediaClass}
        sizes="(max-width: 1079px) 0px, (max-width: 1229px) 400px, (max-width: 1429px) 440px, 508px"
      />

      {slide.enable_video && slide.video_link && (
        <button
          type="button"
          aria-label="Play video"
          onClick={() => onVideoPlay(slide.video_link)}
          className="absolute inset-0 z-10 flex cursor-pointer items-center justify-center"
        >
          <span className="flex size-[56px] items-center justify-center rounded-full bg-black/50 backdrop-blur-sm">
            <span className="ml-[4px] border-y-[10px] border-l-[16px] border-y-transparent border-l-white" />
          </span>
        </button>
      )}
    </div>
  );

  return (
    <div
      className={cn(
        "relative w-full overflow-hidden rounded-[40px]",
        /* The carousel needs a FIXED height or slides of differing ratios would
           resize the box on every rotation. A lone video has nothing to jump
           against, so it takes a max instead and the box follows the video —
           which removes the letterboxed gap under it. */
        /* Square at every breakpoint: the column is 400 / 440 / 508 wide, so
           `aspect-square` yields exactly the design's 400x400, 440x440 and
           508x508 with integer heights — no separate height ladder to drift. */
        "aspect-square",
      )}
    >
      {/* Widget-level video replaces the carousel entirely — no slides, no
          dots — mirroring Banner 2's Background Video. */}
      {video ? (
        <video
          className="h-full w-full rounded-[40px] object-cover"
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
          src={video}
        />
      ) : (
        <>
      {/* One slide renders directly: Embla initialises after hydration and
          positions slides with transforms, which delays the paint of what is
          the section's LCP element. With nothing to slide, skip it — the same
          reasoning Banner 2 documents. */}
      {slides.length > 1 ? (
        <Carousel1
          autoplayDelay={CAROUSEL_INTERVAL_MS}
          options={{ loop: true, duration: 45 }}
          onSlideChange={setIndex}
          navigationButtonClassName="hidden"
        >
          {slides.map(renderSlide)}
        </Carousel1>
      ) : (
        slides.map(renderSlide)
      )}

      {/* Dots per the design: 37.5x7.5 pill for the active slide, 7.5px for the
          rest, centred over the media's lower edge. Hidden for a single slide. */}
      {slides.length > 1 && (
        <div className="absolute bottom-[17.5px] left-1/2 z-20 flex -translate-x-1/2 items-center gap-[6px]">
          {slides.map((slide: any, i: number) => (
            <span
              key={slide.id ?? i}
              className={cn(
                "h-[7.5px] rounded-full transition-all",
                i === index ? "w-[37.5px] bg-white" : "w-[7.5px] bg-white/50",
              )}
            />
          ))}
        </div>
      )}
        </>
      )}
    </div>
  );
};

/**
 * Alumni + rating strip beneath the media.
 *
 * Belongs to the media option only — the PRD removes it when the lead form takes
 * the slot, and on mobile (where the form always wins) it never renders. Each
 * half is independently toggleable, which is what separates the two 1430 design
 * frames.
 */
const AboutRatingStrip = ({
  avatars,
  showLearners,
  learnersDescription,
  learnersDescriptionColor,
  showRating,
  ratingNumber,
  ratingNumberColor,
  ratingText,
  ratingColor,
}: {
  avatars: any[];
  showLearners: boolean;
  learnersDescription?: string;
  learnersDescriptionColor?: string;
  showRating: boolean;
  ratingNumber?: string;
  ratingNumberColor?: string;
  ratingText?: string;
  ratingColor?: string;
}) => {
  const hasLearners =
    showLearners && (Boolean(learnersDescription) || avatars.length > 0);
  const hasRating = showRating && Boolean(ratingNumber);
  if (!hasLearners && !hasRating) return null;

  return (
    /* Two centred rows, not one — avatars + alumni label above, stars + rating
       below. Sits 24px under the media at every breakpoint. */
    <div className="mt-[24px] flex w-full flex-col items-center gap-[16px] md:gap-[20px] lg:gap-[12px]">
      {hasLearners && (
        <div className="flex items-center gap-[10px]">
          {avatars.length > 0 && (
            <div className="flex shrink-0 items-center">
              {avatars.map((item: any, index: number) => (
                <div
                  key={item.id ?? index}
                  className={cn(
                    "relative size-[20px] overflow-hidden rounded-full border border-white/60 md:size-[30px] lg:size-[40px]",
                    // Overlap is ~44% of the avatar, per the design's cluster widths.
                    index > 0 && "-ml-[9px] md:-ml-[13px] lg:-ml-[18px]",
                  )}
                >
                  <Image
                    src={item.image}
                    alt=""
                    fill
                    sizes="40px"
                    className="object-cover"
                  />
                </div>
              ))}
            </div>
          )}
          {learnersDescription && (
            <div
              className="whitespace-nowrap text-[14px] leading-none md:text-[15px] lg:text-[18px]"
              style={{ color: learnersDescriptionColor }}
              dangerouslySetInnerHTML={{ __html: learnersDescription }}
            />
          )}
        </div>
      )}

      {hasRating && (
        <div className="flex items-center gap-[10px]">
          <StarRating rating={parseFloat(ratingNumber as string) || 0} />
          <span
            className="whitespace-nowrap text-[14px] font-semibold leading-none md:text-[15px] lg:text-[18px]"
            style={{ color: ratingNumberColor }}
          >
            {ratingNumber}
          </span>
          {ratingText && (
            <span
              className="whitespace-nowrap text-[14px] leading-none md:text-[15px] lg:text-[18px]"
              style={{ color: ratingColor }}
            >
              {ratingText}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

const About2 = ({ data }: { data: any }) => {
  const { data: sectionData } = data || {};
  /* 1080, not 800: this gate decides which side of the slot renders, and the
     column itself is `hidden sm:flex` where `sm` is 1080. At 800 the two
     disagreed, so between 800 and 1079 the mobile form had already stopped
     rendering while the desktop column was still hidden — the slot showed
     nothing at all. Matching the CSS breakpoint closes that gap. */
  const isMobile = useIsMobile(1080);
  const ref = useRef<HTMLElement>(null);

  const leaddropFormVariant = useSelector(
    (state: RootState) => state.leaddropform.variant,
  );

  const leadDropFormRow = useSelector((state: RootState) => {
    const blob = state.leaddropform.lead_drop_form as
      | { formData?: Record<string, unknown> }
      | null
      | undefined;
    return blob?.formData ?? null;
  });

  const LeaddropFormComponent = useMemo(() => {
    if (!leaddropFormVariant) return null;
    return LEAD_DROP_FORM_SECTION_VARIANTS[leaddropFormVariant] ?? null;
  }, [leaddropFormVariant]);

  const LeaddropFormMobileCtaComponent = useMemo(() => {
    if (!leaddropFormVariant) return null;
    return LEAD_DROP_FORM_MOBILE_CTA_VARIANTS[leaddropFormVariant] ?? null;
  }, [leaddropFormVariant]);

  const activeMetadata = useMemo(() => {
    if (!sectionData?.metadata) return [];
    return [...sectionData.metadata]
      .filter((item: any) => item.status)
      .sort((a: any, b: any) => (a.priority || 0) - (b.priority || 0));
  }, [sectionData?.metadata]);

  const metadataRows = useMemo(() => {
    const rows: any[][] = [];
    for (let i = 0; i < activeMetadata.length; i += 2) {
      rows.push(activeMetadata.slice(i, i + 2));
    }
    return rows;
  }, [activeMetadata]);

  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current) {
  //     FireWidgetViewedEvent(ref, "about_section");
  //   }
  // }, []);

  if (!sectionData?.status) {
    return null;
  }

  const {
    alt_image,
    background_color = "#000000",
    backvariant = "variant_1",
    component_id,
    createdAt,
    created_by,
    description = "",
    description_text_color = "rgba(255, 255, 255, 0.9)",
    desktop_image,
    enable_animation,
    enable_lead_form,
    gradient_color_1 = "#00D4EB",
    gradient_color_2 = "#000000",
    gradient_color_3 = "#000000",
    gradient_color_4 = "#5345C3",
    id,
    is_deleted,
    is_open_leaddrop,
    metadata_1 = [],
    metadata_2 = [],
    metadata_3 = [],
    mobile_image,
    show_image,
    show_learners,
    learners_description,
    learners_description_color = "#FFFFFF",
    show_rating,
    rating_number,
    rating_number_color = "#FFFFFF",
    rating_text,
    rating_color = "#FFFFFF",
    page_id,
    read_more_background_color = "#FFFFFF",
    read_more_color = "#BE2330",
    read_more_text = "Read More",
    status,
    sub_text,
    title,
    title_1 = "Program",
    title_1_color = "rgba(255, 255, 255, 0.8)",
    title_2 = "Overview",
    title_2_color = "#FFFFFF",
    title_2_gradient_angle = "90deg",
    title_2_gradient_end_color = "#02A7B6",
    title_2_gradient_start_color = "#FFFFFF",
    title_text_color = "#000000",
    updatedAt,
    updated_by,
    variant = "2",
    video_link,
  } = sectionData || {};

  const isAnimationEnabled =
    enable_animation === true ||
    enable_animation === 1 ||
    enable_animation === "1";

  /**
   * Right-hand slot: media (image / video) OR the inline lead form — never both.
   *
   * Desktop-only. Below 1080 the lead form always wins and `show_image` is
   * ignored, so mobile output is unchanged. The rating / alumni strip belongs to
   * the media option, so it never appears on mobile either.
   *
   * MySQL TINYINT(1) arrives as 0/1, "0"/"1" or a boolean depending on driver,
   * so coerce rather than trusting truthiness — "0" is a truthy string.
   */
  const toFlag = (v: unknown) => v === true || v === 1 || v === "1";
  const showMedia = toFlag(show_image);
  const showLearners = toFlag(show_learners);
  const showRating = toFlag(show_rating);

  /** `useIsMobile` stays null until after mount, so this is false during SSR. */
  const isDesktopViewport = isMobile === false;
  const leadFormTakesSlot = Boolean(enable_lead_form) && isDesktopViewport;

  /** Carousel slides; empty falls back to the single `desktop_image`. */
  const [videoOpen, setVideoOpen] = useState(false);
  const [activeVideoUrl, setActiveVideoUrl] = useState("");

  const activeCarouselSlides = useMemo(() => {
    if (!Array.isArray(metadata_3)) return [];
    return [...metadata_3]
      .filter((item: any) => item?.status && item?.image)
      .sort((a: any, b: any) => (a.priority || 0) - (b.priority || 0))
      .slice(0, MAX_CAROUSEL_SLIDES);
  }, [metadata_3]);

  const activeLearnerAvatars = useMemo(() => {
    if (!Array.isArray(metadata_2)) return [];
    return [...metadata_2]
      .filter((item: any) => item?.status && item?.image)
      .sort((a: any, b: any) => (a.priority || 0) - (b.priority || 0))
      .slice(0, MAX_LEARNER_AVATARS);
  }, [metadata_2]);

  const getGradientBackground = () => {
    switch (backvariant) {
      case "variant_1":
        return `linear-gradient(253deg, ${hexToRgba(gradient_color_1, 0.33)} 0%, ${hexToRgba(gradient_color_2, 0.66)} 24.68%, ${hexToRgba(gradient_color_3, 0.66)} 79.09%, ${hexToRgba(gradient_color_4, 0.46)} 100%), ${background_color}`;
      case "variant_2":
        return `linear-gradient(292deg, ${hexToRgba(gradient_color_1, 0.33)} 0%, ${hexToRgba(gradient_color_2, 0.66)} 24.68%, ${hexToRgba(gradient_color_3, 0.66)} 79.09%, ${hexToRgba(gradient_color_4, 0.46)} 100%), ${background_color}`;
      case "variant_3":
        return background_color;
      default:
        return `linear-gradient(253deg, ${hexToRgba(gradient_color_1, 0.33)} 0%, ${hexToRgba(gradient_color_2, 0.66)} 24.68%, ${hexToRgba(gradient_color_3, 0.66)} 79.09%, ${hexToRgba(gradient_color_4, 0.46)} 100%), ${background_color}`;
    }
  };

  return (
    <section
      ref={ref}
      className="relative w-full overflow-hidden"
      style={{ backgroundColor: background_color }}
    >
      {/* Background Gradient Overlay */}
      <div
        className="absolute inset-0 z-0"
        style={{ background: getGradientBackground() }}
      />
      {/* Content Container */}
      <div
        className={cn(
          "relative z-10 mx-auto",
          "flex flex-col items-center gap-[24px] px-[24px] py-[40px]",
          /* `justify-between` pushes the two columns to the outer edges. The
             left column is `flex-1` but capped by its max-width, so without
             this any leftover width collected after the right column instead
             of between them. `gap` still applies as a minimum separation. */
          "sm:max-w-[1080px] sm:flex-row sm:items-start sm:justify-between sm:gap-[40px] sm:py-[40px] sm:pl-[60px] sm:pr-[40px]",
          "md:max-w-[1230px] md:py-[50px] md:pr-[60px]",
          "lg:max-w-[1430px] lg:py-[60px] lg:pl-[80px] lg:pr-[70px]",
          "xl:max-w-[1750px] xl:gap-[110px] xl:px-[140px] xl:py-[80px]",
        )}
      >
        {/* Left Column - Content */}
        <div className="contents sm:block sm:max-w-[500px] sm:flex-1 md:max-w-[620px] lg:max-w-[732px] xl:max-w-[800px]">
          {/* Section Title — one <h2> for both lines (SEO reads them as one heading).
              Spans, not <p>: a heading may only hold phrasing content; `block` keeps
              each on its own line as the <p>s did. */}
          <h2 className="order-1 w-full sm:order-none sm:mb-[30px] lg:mb-[24px]">
            {title_1 && (
              <span className="white-title-wf block" style={{ color: title_1_color }}>
                {title_1.slice(0, 15)}
              </span>
            )}
            {title_2 && (
              <span
                className="gradient-title-denton block"
                style={{
                  backgroundImage: `linear-gradient(${title_2_gradient_angle}, ${title_2_gradient_start_color}, ${title_2_gradient_end_color})`,
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  color: "transparent",
                }}
              >
                {title_2.slice(0, 45)}
              </span>
            )}
          </h2>

          {/* Description */}
          {description && (
            <RevealOnScroll
              className="order-2 w-full sm:order-none"
              enableAnimation={isAnimationEnabled}
            >
              <p
                className={cn(
                  "font-wf-visual-sans font-semibold leading-[1.5]",
                  "text-[14px]",
                  "sm:mb-[20px] sm:text-[15px]",
                  "md:mb-[34px] md:text-[16px]",
                  "lg:mb-[60px] lg:text-[20px]",
                )}
                style={{ color: description_text_color }}
              >
                {description}
              </p>
            </RevealOnScroll>
          )}

          {/* Key Highlights Grid */}
          {metadataRows.length > 0 && (
            <div className="order-4 flex w-full flex-col gap-[20px] sm:order-none sm:gap-[32px] md:gap-[40px]">
              {metadataRows.map((row: any[], rowIdx: number) => (
                <div
                  key={rowIdx}
                  className="flex flex-col gap-[20px] sm:flex-row sm:gap-[24px] lg:gap-[40px]"
                >
                  {row.map((item: any) => (
                    <div
                      key={item.id}
                      className="sm:flex sm:flex-1 sm:flex-col"
                    >
                      <RevealOnScroll enableAnimation={isAnimationEnabled}>

                      <p
                        className={cn(
                          "font-merriweather font-medium leading-[1.5]",
                          "text-[24px]",
                          "sm:text-[30px] sm:tracking-[0.9px]",
                          "md:text-[34px] md:tracking-[1.02px]",
                          "lg:text-[40px] lg:tracking-[1.2px]",
                        )}
                        style={{ color: item.title_color || "#00d4eb" }}
                      >
                        {item.title.slice(0, 15)}
                      </p>
                      </RevealOnScroll>
                      <RevealOnScroll
                        duration={1000}
                        y={80}
                        enableAnimation={isAnimationEnabled}
                      >

                      <p
                        className={cn(
                          "font-wf-visual-sans font-medium leading-140 -tracking-012",
                          "text-[12px]",
                          "sm:text-[12px]",
                          "md:text-[14px]",
                          "lg:text-[16px]",
                        )}
                        style={{
                          color: item.subtitle_color || "rgba(255,255,255,0.8)",
                        }}
                      >
                        {item.subtitle.slice(0, 45)}
                      </p>
                        </RevealOnScroll>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Mobile: the full inline lead form, per the mobile design frame.
            The PRD's "mobile always shows the Lead Form" means the form itself,
            not the CTA strip this used to render — mobile ignores the desktop
            slot choice entirely, so `show_image` is deliberately not consulted
            here. Falls back to the mapped CTA component when the tenant's form
            variant has no inline component, so no tenant loses its lead capture. */}
        {Boolean(enable_lead_form) && isMobile && (
          <RevealOnScroll className="order-3 w-full" enableAnimation={isAnimationEnabled}>
            <div className="flex w-full flex-col items-center">
              {LeaddropFormComponent ? (
                leaddropFormVariant ===
                  LEAD_DROP_FORM_VARIANTS.LEAD_DROP_FORM_6 && leadDropFormRow ? (
                  <LeaddropFormComponent data={{ data: leadDropFormRow }} />
                ) : (
                  <LeaddropFormComponent />
                )
              ) : LeaddropFormMobileCtaComponent ? (
                <LeaddropFormMobileCtaComponent />
              ) : null}
            </div>
          </RevealOnScroll>
        )}

        {/* ─── Right Column (desktop only) — media OR lead form ───
            The column keeps its width whichever option is active, and renders
            even before `isMobile` resolves, so the left column can be
            server-rendered without the right side shifting the layout when it
            arrives. Below 1080 it stays `hidden` and the mobile CTA above takes
            over — the slot choice never reaches mobile. */}
        <div
          className={cn(
            "hidden overflow-hidden",
            "sm:flex sm:shrink-0 sm:flex-col",
            // The two options do NOT share a column width — the lead form's
            // ladder comes from its own frames, the media's from the media
            // frames (400 / 440 / 508, capped at 508 from 1430 up).
            //
            // `self-stretch` is what makes `justify-center` mean anything: the
            // row is `sm:items-start`, so without it this column shrinks to its
            // content height and there is no free space left to centre within.
            // Stretching is scoped to the media slot so the lead form keeps its
            // existing top-aligned position.
            showMedia
              ? "sm:w-[400px] sm:justify-center sm:self-stretch md:w-[440px] lg:w-[508px]"
              : // 1430 is 480, not 475: this column is `overflow-hidden`, and lead
                // form 2001's card is 480px wide there by design (334/380/440/480).
                // At 475 the card overflowed by 5px and had its right edge — and
                // its rounded corner — clipped. Narrower variants are unaffected;
                // they are `ml-auto` inside, so they just gain 5px of free space.
                "sm:w-[440px] md:w-[450px] lg:w-[480px] xl:w-[560px]",
          )}
        >
          {showMedia ? (
            <RevealOnScroll
              x={400}
              scaleStart={0.5}
              enableAnimation={isAnimationEnabled}
              className="w-full"
            >
              <AboutMedia
                slides={activeCarouselSlides}
                alt={alt_image}
                video={video_link}
                onVideoPlay={(url) => {
                  setActiveVideoUrl(url);
                  setVideoOpen(true);
                }}
              />
              <AboutRatingStrip
                avatars={activeLearnerAvatars}
                showLearners={showLearners}
                learnersDescription={learners_description}
                learnersDescriptionColor={learners_description_color}
                showRating={showRating}
                ratingNumber={rating_number}
                ratingNumberColor={rating_number_color}
                ratingText={rating_text}
                ratingColor={rating_color}
              />
            </RevealOnScroll>
          ) : leadFormTakesSlot && LeaddropFormComponent ? (
            <RevealOnScroll
              x={400}
              scaleStart={0.5}
              enableAnimation={isAnimationEnabled}
              /* NOTE: RevealOnScroll drops className entirely when animation
                 is off (it returns `children` directly), so alignment must NOT
                 live here — see the `ml-auto` wrapper below. */
              className="w-full"
            >
              {/* `ml-auto` here, not on RevealOnScroll: that component returns
                  `children` unchanged when animation is disabled, dropping any
                  className it was given. This div always renders. */}
              <div className="ml-auto w-fit">
                {leaddropFormVariant ===
                  LEAD_DROP_FORM_VARIANTS.LEAD_DROP_FORM_6 && leadDropFormRow ? (
                  <LeaddropFormComponent data={{ data: leadDropFormRow }} />
                ) : (
                  <LeaddropFormComponent />
                )}
              </div>
            </RevealOnScroll>
          ) : (
            /* Pre-mount, or the lead form is on but unresolvable: hold the
               column's height so nothing jumps when the real content lands. */
            <div aria-hidden className="min-h-[620px] w-full" />
          )}
        </div>
      </div>

      {/* Slide videos supplied as YouTube links open in the shared overlay,
          exactly as Banner 2 handles them. */}
      {activeVideoUrl && (
        <YouTubeVideoOverlay
          isOpen={videoOpen}
          onClose={() => {
            setVideoOpen(false);
            setActiveVideoUrl("");
          }}
          videoUrl={activeVideoUrl}
        />
      )}
    </section>
  );
};

export default About2;
